import { useEffect, useRef } from 'react';
import { overlayRectFor } from '../../../utils/anchoredOverlayRect';
import { textsLookLikeTypingLag } from './syntaxHighlightHelpers';

/** Effects that keep the editable in step with value, variables and the expanded overlay. */
export function useHighlightSync({
  value,
  selectedEnvironment,
  workspaceVariables,
  globalVariables,
  collectionVariables,
  folderVariables,
  forceSyncNonce,
  expandOnOverflow,
  setEditingVariable,
  isExpanded,
  setIsExpanded,
  setOverlayRect,
  editableRef,
  anchorRef,
  overlayRef,
  editingVariableRef,
  isUpdatingRef,
  lastValueRef,
  lastRenderedTextRef,
  variableSourcesVersionRef,
  renderHighlightedContent,
  getPlainText,
  getSelectionOffsets,
}) {
  // Initialize content when component mounts or value changes externally
  useEffect(() => {
    if (!editableRef.current) return;
    
    // Don't update if we're currently updating
    if (isUpdatingRef.current) {
      return;
    }

    const currentText = getPlainText(editableRef.current);
    const nextValue = value || '';

    // While focused, skip sync only when props already match the editor (normal typing).
    if (document.activeElement === editableRef.current && nextValue === currentText) {
      lastValueRef.current = currentText;
      lastRenderedTextRef.current = currentText;
      return;
    }

    // While focused: do not overwrite the DOM with a stale/shorter/longer `value` during the same
    // keystroke pipeline — wait until props catch up or blur finalizes (fixes scrambled text).
    if (
      document.activeElement === editableRef.current &&
      nextValue !== currentText &&
      textsLookLikeTypingLag(nextValue, currentText)
    ) {
      return;
    }

    // Apply external / blur-synced value to the editor
    if (nextValue !== currentText) {
      lastValueRef.current = nextValue;
      renderHighlightedContent(nextValue);

      requestAnimationFrame(() => {
        if (!editableRef.current || editableRef.current.childNodes.length === 0) return;
        // Only reposition cursor when this element is actually focused.
        // Running selection.addRange on a non-focused contenteditable steals
        // focus away from whatever the user is actively typing in.
        if (document.activeElement !== editableRef.current) return;
        const range = document.createRange();
        const selection = window.getSelection();
        range.selectNodeContents(editableRef.current);
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
      });
    }
  }, [value, renderHighlightedContent, getPlainText, editableRef, isUpdatingRef, lastRenderedTextRef, lastValueRef]);

  // cURL import sets `value` but paste is swallowed — DOM can stay empty while lag detection skips sync.
  // Intentionally omit `value` from deps: only run when nonce bumps (same render already has the new URL).
  // Guard on the nonce actually CHANGING (not just being truthy): `renderHighlightedContent` is in
  // the dep list and its identity churns whenever the variable-list props get new identities (every
  // parent re-render — i.e. every keystroke once the tab has synced once, leaving the nonce at ≥1).
  // Without the change-guard this effect force-rewrote the DOM on each keystroke with no caret
  // restore, collapsing the caret to offset 0 / end — typing `{{var}}` scrambled the URL.
  const prevForceSyncNonceRef = useRef(forceSyncNonce);
  useEffect(() => {
    if (forceSyncNonce === prevForceSyncNonceRef.current) return;
    prevForceSyncNonceRef.current = forceSyncNonce;
    if (!editableRef.current) return;

    isUpdatingRef.current = false;
    const nextValue = value || '';
    lastValueRef.current = nextValue;
    renderHighlightedContent(nextValue, null, true);

    requestAnimationFrame(() => {
      if (!editableRef.current || editableRef.current.childNodes.length === 0) return;
      // Do not steal focus from another control (e.g. sidebar rename input).
      if (document.activeElement !== editableRef.current) return;
      const range = document.createRange();
      const selection = window.getSelection();
      range.selectNodeContents(editableRef.current);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- nonce-only: avoid re-sync on every keystroke
  }, [forceSyncNonce, renderHighlightedContent]);

  // Repaint highlights whenever the resolved variable data actually changes.
  useEffect(() => {
    if (!editableRef.current) return;

    const sourceVersion = JSON.stringify({
      folder: (folderVariables || []).map(v => `${v.folderId}:${v.key}:${v.value}:${v.enabled ? 1 : 0}:${v.secret ? 1 : 0}`),
      collection: (collectionVariables || []).map(v => `${v.key}:${v.value}:${v.enabled ? 1 : 0}:${v.secret ? 1 : 0}`),
      environment: (selectedEnvironment?.variables || []).map(v => `${v.key}:${v.value}:${v.enabled ? 1 : 0}:${v.secret ? 1 : 0}`),
      workspace: (workspaceVariables || []).map(v => `${v.key}:${v.value}:${v.enabled ? 1 : 0}:${v.secret ? 1 : 0}`),
      global: (globalVariables || []).map(v => `${v.key}:${v.value}:${v.enabled ? 1 : 0}:${v.secret ? 1 : 0}`)
    });

    if (sourceVersion === variableSourcesVersionRef.current) return;
    variableSourcesVersionRef.current = sourceVersion;

    const currentText = getPlainText(editableRef.current);
    const isInputFocused = document.activeElement === editableRef.current;
    // Plain KV fields (no {{vars}}): skip DOM repaint while typing — collection variable list
    // updates on every keypress and would scramble the caret (same class of bug as docs editor).
    if (isInputFocused && !/\{\{/.test(currentText)) {
      return;
    }
    const selOffsets = isInputFocused ? getSelectionOffsets() : null;
    renderHighlightedContent(currentText, selOffsets, true);
  }, [selectedEnvironment, workspaceVariables, globalVariables, collectionVariables, folderVariables, getPlainText, getSelectionOffsets, renderHighlightedContent, editableRef, variableSourcesVersionRef]);

  // URL field only: divider border against the History button, matching the same divider on the
  // expanded overlay. Set imperatively (not via a CSS class) because an equal-specificity,
  // equal-!important stylesheet rule here consistently loses to the pre-existing borderless-variant
  // reset for this element despite appearing later in the same stylesheet — setting it directly on
  // the node with explicit priority sidesteps that and is unambiguous. Re-applied via a
  // MutationObserver on <html> so it stays correct across dark-mode toggles without needing a
  // re-render of this field.
  useEffect(() => {
    // Only the collapsed node gets this — the expanded overlay already carries its own matching
    // border-right via CSS on its wrapper (.syntax-highlight-input-overlay).
    if (!expandOnOverflow || isExpanded || !editableRef.current) return;
    const node = editableRef.current;
    const applyBorder = () => {
      const isDark = document.documentElement.classList.contains('dark');
      node.style.setProperty('border-right', `1px solid ${isDark ? '#2e2e33' : '#e5e7eb'}`, 'important');
    };
    applyBorder();
    const observer = new MutationObserver(applyBorder);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, [expandOnOverflow, isExpanded, editableRef]);

  // A resized app window could leave the floating overlay misaligned with its anchor —
  // collapse rather than try to live-reposition it. The user can just refocus.
  useEffect(() => {
    if (!expandOnOverflow || !isExpanded) return;
    const collapse = () => setIsExpanded(false);
    window.addEventListener('resize', collapse);
    return () => window.removeEventListener('resize', collapse);
  }, [expandOnOverflow, isExpanded, setIsExpanded]);

  // Scrolling is different from resizing: the field is still exactly where the user is
  // looking, it has simply moved, so following it is the right answer where collapsing
  // would be a flicker. In a params/headers table the row scrolls sideways under a
  // `position: fixed` overlay that kept its opening coordinates — which is how it ended
  // up drawn across the header row (owner, 22.09.2026).
  //
  // Capture phase, because a scroll event on an element does not bubble. Scrolls that
  // START inside the overlay are its own (the text scrolls past `max-height`) and move
  // nothing.
  useEffect(() => {
    if (!expandOnOverflow || !isExpanded) return;
    const reposition = (e) => {
      if (e.target instanceof Node && overlayRef.current?.contains(e.target)) return;
      const next = overlayRectFor(anchorRef.current);
      // Scrolled out of its container entirely: there is nothing to sit on top of any
      // more, so collapse instead of parking over whatever took its place.
      if (!next) {
        setIsExpanded(false);
        return;
      }
      setOverlayRect((prev) => (prev ? { ...prev, ...next } : prev));
    };
    document.addEventListener('scroll', reposition, true);
    return () => document.removeEventListener('scroll', reposition, true);
  }, [expandOnOverflow, isExpanded, anchorRef, overlayRef, setIsExpanded, setOverlayRect]);

  // URL field only: clicking outside should always return to the collapsed single-line view.
  // Native blur normally handles this, but explicitly blurring on an outside pointerdown is a
  // safety net across WebView engines where blur-on-DOM-removal timing can be unreliable —
  // this just triggers the existing handleBlur commit/collapse logic, nothing new to maintain.
  useEffect(() => {
    if (!expandOnOverflow || !isExpanded) return;
    const handlePointerDown = (e) => {
      // The variable-edit popup (and its portaled scope dropdown) render outside editableRef, so
      // a click inside either would otherwise look like "outside" here — it's a continuation of
      // editing this field, not a click away.
      if (e.target.closest('.var-editor') || e.target.closest('.var-editor-scope-dropdown')) return;
      // Likewise the "To Variable" menu: pressing its item is the step BEFORE the popup opens.
      if (e.target.closest('.shi-selection-menu')) return;
      if (editableRef.current && !editableRef.current.contains(e.target)) {
        // Genuinely outside both the field and its popup — close the popup synchronously (via
        // the ref) so handleBlur's guard doesn't block the collapse.
        editingVariableRef.current = null;
        setEditingVariable(null);
        // When the variable popup is open, focus already moved to the popup's input, so the
        // editable is no longer focused and editableRef.blur() would be a no-op — handleBlur
        // (which collapses) would never fire and the field would stay stuck expanded. Collapse
        // directly in that case. When the editable itself still holds focus (e.g. the last click
        // was on plain text), blur() runs handleBlur, which commits the final text and collapses.
        if (document.activeElement === editableRef.current) {
          editableRef.current.blur();
        } else {
          setIsExpanded(false);
        }
      }
    };
    document.addEventListener('mousedown', handlePointerDown, true);
    return () => document.removeEventListener('mousedown', handlePointerDown, true);
  }, [expandOnOverflow, isExpanded, editableRef, editingVariableRef, setEditingVariable, setIsExpanded]);
}
